import React from 'react';
import { FaPlay, FaArrowRight, FaChartLine, FaCode, FaMobile, FaCloud, FaShieldAlt, FaSearch, FaShoppingCart, FaServer, FaDatabase, FaLaptopCode } from 'react-icons/fa';
import Lottie from 'lottie-react';
import anime from '../anime/ass-marketing.json';

const Hero = ({ 
  mode = "combined", // "it", "marketing", or "combined"
  title = null,
  subtitle = null
}) => {
  // IT Services
  const itServices = [
    {
      icon: FaCode,
      title: "Web Development",
      description: "Custom websites & web applications",
    },
    {
      icon: FaServer,
      title: "Cloud Solutions",
      description: "Scalable cloud infrastructure"
    },
    {
      icon: FaShieldAlt,
      title: "Cyber Security",
      description: "Complete digital protection"
    },
    {
      icon: FaDatabase,
      title: "Data Solutions",
      description: "Database management & analytics"
    }
  ];

  // Digital Marketing Services
  const marketingServices = [
    {
      icon: FaSearch,
      title: "SEO Optimization",
      description: "Rank higher in search results"
    },
    {
      icon: FaCloud,
      title: "Social Media Marketing",
      description: "Engage your audience effectively"
    },
    {
      icon: FaShoppingCart,
      title: "E-commerce Solutions",
      description: "Boost your online sales"
    },
    {
      icon: FaChartLine,
      title: "Analytics & ROI",
      description: "Data-driven marketing strategies"
    }
  ];

  // Combined services for home page
  const combinedServices = [
    {
      icon: FaCode,
      title: "Web Development",
      description: "Custom websites & web applications"
    },
    {
      icon: FaMobile,
      title: "Digital Marketing",
      description: "SEO, Social Media & PPC campaigns"
    },
    {
      icon: FaCloud,
      title: "Cloud Solutions",
      description: "Scalable cloud infrastructure"
    },
    {
      icon: FaShieldAlt,
      title: "Cyber Security",
      description: "Complete digital protection"
    }
  ];

  const getServices = () => {
    switch (mode) {
      case "it":
        return itServices;
      case "marketing":
        return marketingServices;
      case "combined":
      default:
        return combinedServices;
    }
  };

  const getMainTitle = () => {
    if (title) return title;
    
    switch (mode) {
      case "it":
        return (
          <>
            Transform Your{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-cyan-500 bg-clip-text text-transparent block pb-3">
              Technology
            </span>
          </>
        );
      case "marketing":
        return (
          <>
            Amplify Your{' '}
            <span className="bg-gradient-to-r from-orange-400 via-pink-500 to-red-500 bg-clip-text text-transparent block pb-3">
              Digital Presence
            </span>
          </>
        );
      case "combined":
      default:
        return (
          <>
            Transform Your{' '}
            <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-orange-500 bg-clip-text text-transparent block">
              Business
            </span>
          </>
        );
    }
  };

  const getSubtitle = () => {
    if (subtitle) return subtitle;
    
    switch (mode) {
      case "it":
        return (
          <>
            Complete <span className="text-blue-400 font-semibold">IT solutions</span> and Cutting-Edge{' '}
            <span className="text-cyan-400 font-semibold">Technology Services</span>
            that drive innovation and efficiency.
          </>
        );
      case "marketing":
        return (
          <>
            Powerful <span className="text-orange-400 font-semibold">Digital Marketing</span> strategies and data-driven{' '}
            <span className="text-pink-400 font-semibold">Growth Solutions</span>
            that maximize your online impact.
          </>
        );
      case "combined":
      default:
        return (
          <>
            Complete <span className="text-blue-400 font-semibold">IT solutions</span> and powerful{' '}
            <span className="text-orange-400 font-semibold">Digital Marketing</span> strategies
            that drive growth and innovation.
          </>
        );
    }
  };

  const getGradientColors = () => {
    switch (mode) {
      case "it":
        return "from-blue-500 to-cyan-600";
      case "marketing":
        return "from-orange-500 to-pink-600";
      case "combined":
      default:
        return "from-blue-500 to-purple-600";
    }
  };

  const getGlowColors = () => {
    switch (mode) {
      case "it":
        return {
          primary: "blue-500",
          secondary: "cyan-500"
        };
      case "marketing":
        return {
          primary: "orange-500",
          secondary: "pink-500"
        };
      case "combined":
      default:
        return {
          primary: "blue-500",
          secondary: "orange-500"
        };
    }
  };

  const getExpertiseTitle = () => {
    switch (mode) {
      case "it":
        return "Our IT Services:";
      case "marketing":
        return "Our Marketing Services:";
      case "combined":
      default:
        return "Our Expertise Includes:";
    }
  };

  const glowColors = getGlowColors();

  return (
    <>
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        
        @keyframes floatReverse {
          0%, 100% { transform: translateY(0) rotate(0); }
          50% { transform: translateY(20px) rotate(-5deg); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(10px); }
        }
        
        @keyframes staggerChildren {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-fade-in {
          animation: fadeIn 2s ease-out forwards;
        }
        
        .animate-slide-in-right {
          animation: slideInRight 1s ease-out 0.5s forwards;
          opacity: 0;
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-float-reverse {
          animation: floatReverse 4s ease-in-out 1s infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s ease-in-out infinite;
        }
        
        .stagger-animation > *:nth-child(1) { animation-delay: 0.1s; }
        .stagger-animation > *:nth-child(2) { animation-delay: 0.2s; }
        .stagger-animation > *:nth-child(3) { animation-delay: 0.3s; }
        .stagger-animation > *:nth-child(4) { animation-delay: 0.4s; }
        .stagger-animation > *:nth-child(5) { animation-delay: 0.5s; }
        .stagger-animation > *:nth-child(6) { animation-delay: 0.6s; }
        
        .stagger-item {
          opacity: 0;
          animation: staggerChildren 0.8s ease-out forwards;
        }
        
        .hover-scale:hover {
          transform: scale(1.05);
        }
        
        .hover-glow:hover {
          box-shadow: 0 15px 30px -10px rgba(59, 130, 246, 0.4);
        }
        
        .transition-all-custom {
          transition: all 0.3s ease;
        }
      `}</style>

      <section id="home" className="relative bg-gradient-to-br from-black via-gray-900 to-black text-white flex items-center overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />

          <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_95%,rgba(255,255,255,0.03)_100%)] bg-[size:100px_100px] animate-fade-in" />

          {/* Glow Effects */}
          <div className={`absolute top-1/4 left-1/4 w-96 h-96 bg-${glowColors.primary} rounded-full filter blur-3xl opacity-10`} />
          <div className={`absolute bottom-1/4 right-1/4 w-80 h-80 bg-${glowColors.secondary} rounded-full filter blur-3xl opacity-5 animate-fade-in`} style={{animationDelay: '0.5s'}} />
        </div>

        <div className="container mx-auto px-4 sm:px-6 py-16 relative z-10">
          <div className="max-w-7xl mx-auto stagger-animation">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-5 lg:gap-16">
              {/* Left Content - Text Section */}
              <div className="w-full lg:w-1/2 text-center lg:text-left">

                {/* Main Heading */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight stagger-item">
                  {getMainTitle()}
                </h1>

                {/* Subtitle */}
                <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0 stagger-item">
                  {getSubtitle()}
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-4 stagger-item">
                  <button className={`group bg-gradient-to-r ${getGradientColors()} text-white px-5 py-2 rounded-lg font-semibold text-base hover:shadow-xl transition-all-custom hover-scale hover-glow relative overflow-hidden`} onClick={() => (window.location.href = "/contact-us")}>
                    <span className="relative z-10 flex items-center gap-2 justify-center">
                      Get Started
                      <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </button>

                  <button className="group flex items-center gap-2 border-2 border-gray-600 text-gray-300 px-5 py-2 rounded-lg font-medium text-base hover:border-orange-500 hover:text-orange-400 transition-all-custom hover-scale justify-center" onClick={() => (window.location.href = "/contact-us")}>
                    <div className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center group-hover:bg-orange-500 transition-colors duration-300">
                      <FaPlay className="w-3 h-3 ml-1" />
                    </div>
                    Watch Demo
                  </button>
                </div>

                {/* Services Quick Overview */}
                <div className="mb-12 stagger-item">
                  <h3 className="text-lg font-semibold text-gray-300 mb-6 text-center lg:text-left">{getExpertiseTitle()}</h3>
                  <div className="grid grid-cols-2 gap-3 max-w-md mx-auto lg:mx-0">
                    {getServices().map((service, index) => (
                      <div
                        key={service.title}
                        className="flex items-center gap-2 bg-gray-800 bg-opacity-50 px-4 py-3 rounded-xl border border-gray-700 backdrop-blur-sm hover:border-blue-500 transition-all-custom hover-scale stagger-item"
                        style={{animationDelay: `${0.7 + index * 0.1}s`}}
                      >
                        <service.icon className="w-12 h-12 text-blue-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-200">{service.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Content - Animation Section */}
              <div className="w-full lg:w-1/2 flex justify-center">
                <div className="relative max-w-lg w-full animate-slide-in-right">
                  <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-6 border border-gray-700 shadow-2xl backdrop-blur-sm transition-all-custom hover-scale">
                    {/* Lottie Animation */}
                    <div className="relative z-0">
                      <Lottie
                        animationData={anime}
                        loop={true}
                        autoplay={true}
                        style={{
                          width: '100%',
                          height: 300,
                        }}
                        rendererSettings={{
                          preserveAspectRatio: 'xMidYMid slice'
                        }}
                      />
                    </div>

                    {/* Dual Focus Cards */}
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      <div className={`${
                        mode === "it" ? "bg-cyan-900 bg-opacity-30 border-cyan-700" : 
                        mode === "marketing" ? "bg-orange-900 bg-opacity-30 border-orange-700" : 
                        "bg-orange-900 bg-opacity-30 border-orange-700"
                      } p-4 rounded-xl border backdrop-blur-sm transition-all-custom hover-scale`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${
                            mode === "it" ? "bg-cyan-500" : 
                            mode === "marketing" ? "bg-orange-500" : 
                            "bg-orange-500"
                          } rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <FaChartLine className="w-5 h-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-white font-semibold text-sm truncate">
                              {mode === "it" ? "IT Solutions" : "Digital Marketing"}
                            </h4>
                            <p className={`${
                              mode === "it" ? "text-cyan-200" : 
                              mode === "marketing" ? "text-orange-200" : 
                              "text-orange-200"
                            } text-xs truncate`}>
                              {mode === "it" ? "Development & Security" : "Growth & Analytics"}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-blue-900 bg-opacity-30 p-4 rounded-xl border border-blue-700 backdrop-blur-sm transition-all-custom hover-scale">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FaLaptopCode className="w-5 h-5 text-white" />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-white font-semibold text-sm truncate">
                              {mode === "marketing" ? "Marketing Analytics" : "Technology"}
                            </h4>
                            <p className="text-blue-200 text-xs truncate">
                              {mode === "marketing" ? "Data & Insights" : "Innovation & Solutions"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Elements */}
                  <div className={`absolute -top-4 -right-4 w-16 h-16 bg-${glowColors.primary} bg-opacity-20 rounded-2xl border border-${glowColors.primary} backdrop-blur-sm flex items-center justify-center animate-float`}>
                    <FaCode className={`w-6 h-6 text-${glowColors.primary}`} />
                  </div>

                  <div className={`absolute -bottom-4 -left-4 w-14 h-14 bg-${glowColors.secondary} bg-opacity-20 rounded-2xl border border-${glowColors.secondary} backdrop-blur-sm flex items-center justify-center animate-float-reverse`}>
                    <FaChartLine className={`w-5 h-5 text-${glowColors.secondary}`} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        
      </section>
    </>
  );
};

export default Hero;